import React, { Component, Fragment } from 'react';
import { DatasetPresence, DatasetPresences, PricesPresence, PricesPresences } from '~/Api/Product';
import { FilterParameterName } from '~/Api/Product/findProducts';
import CountryAutocomplete from '~/CountryAutocomplete';
import FilterParameter from '~/libs/FilterParameter/FilterParameter';
import FilterParameterDescription from '~/libs/FilterParameter/FilterParameterDescription';
import { selectCardinalPlural } from '~/libs/Intl';
import SearchSettingsModal from '~/ProductFilterForm/SearchSettingsModal/SearchSettingsModal';
import { Link } from '~/UI/Button';
import FilterForm from '~/UI/FilterForm/FilterForm';
import Icon from '~/UI/Icon/Icon';

import type GroupedDataSource from '~/Api/DataSource/GroupedDataSource';

import styles from './styles.less';

export type ProductFilterFormFilters = (
	| FilterParameter<PricesPresence, FilterParameterName.pricesPresence>
	| FilterParameter<DatasetPresence, FilterParameterName.productDatasetPresence>
	| FilterParameter<string[], FilterParameterName.datasource>
)[];

interface Props {
	availableParameters: FilterParameterDescription<string | string[]>[];
	filterParameters: ProductFilterFormFilters;
	groupedDataSources: GroupedDataSource[] | null;
	errors: { [key in string]: string[] };
	pricesPresence: PricesPresence | null;
	datasetPresence: DatasetPresence | null;
	selectedGrouppingType: string | null;
	onSearch(searchParameters: ProductFilterFormFilters): void;
	onChange(parameters: ProductFilterFormFilters): void;
	onSubmitSearchDataSourceSettings(params: {
		types: Set<string> | null;
		selectedGrouppingType: string | null;
		groupName: string | null;
		pricesPresence: PricesPresence | null;
		datasetPresence: DatasetPresence | null;
	}): void;
}

interface State {
	showSettingsModal: boolean;
}

export default class ProductFilterForm extends Component<Props, State> {
	static defaultProps = {
		errors: {},
	};

	state = {
		showSettingsModal: false,
	};

	filterFormElement: FilterForm | null = null;

	datasourceSelectPopover: HTMLSpanElement | null = null;

	resolveAvailableParameters(): FilterParameterDescription<string | string[]>[] {
		return this.props.availableParameters.filter(
			(parameter) =>
				parameter.name !== FilterParameterName.datasource &&
				parameter.name !== FilterParameterName.pricesPresence &&
				parameter.name !== FilterParameterName.productDatasetPresence
		);
	}

	renderSettingsButton(
		dataSourceTypes: Set<string> | null,
		pricesPresence: PricesPresence | null,
		datasetPresence: DatasetPresence
	) {
		const selectedSourcesGroup = this.props.selectedGrouppingType
			? (this.props.groupedDataSources || []).find((grouppedSources) => {
					return grouppedSources.id === this.props.selectedGrouppingType;
			  })
			: null;

		const fullGroupSelected = selectedSourcesGroup?.dataSources.length === dataSourceTypes?.size;

		const settings = dataSourceTypes ? (
			<Fragment>
				<strong>{dataSourceTypes.size}</strong>{' '}
				{selectCardinalPlural(dataSourceTypes.size, ['datasource', 'datasources'])}
				<>
					<br />
					{selectedSourcesGroup && fullGroupSelected && (selectedSourcesGroup.name || '')}
				</>
			</Fragment>
		) : null;

		const rows = [
			settings,
			pricesPresence === PricesPresences.atLeastOne ? (
				<Fragment>
					products with <strong>at least one price</strong>
				</Fragment>
			) : null,
			datasetPresence === DatasetPresences.all ? (
				<Fragment>
					<strong>all</strong> products <strong>including removed</strong> from datasets
				</Fragment>
			) : null,
			datasetPresence === DatasetPresences.notPresent ? (
				<Fragment>
					only products <strong>removed</strong> from datasets
				</Fragment>
			) : null,
		].filter((row) => row !== null);

		return (
			<div className={styles.settings}>
				<Link styles={{ button: styles.settingsButton }} onClick={() => this.setState({ showSettingsModal: true })}>
					<Icon name='options-blue'>options</Icon>Search Settings
				</Link>
				<div className={styles.settingsLabel}>
					{/* eslint-disable-next-line react/no-array-index-key */}
					{rows.map((row, i) => (
						<Fragment key={i}>
							{i > 0 ? <br /> : null}
							{row}
						</Fragment>
					))}
				</div>
			</div>
		);
	}

	handleSettingsConfirmed = (
		types: Set<string> | null,
		pricesPresence: PricesPresence | null,
		datasetPresence: DatasetPresence,
		selectedGrouppingType: string | null,
		groupName: string | null
	) => {
		if (!this.filterFormElement) {
			return;
		}

		this.props.onSubmitSearchDataSourceSettings({
			types,
			selectedGrouppingType,
			groupName,
			datasetPresence,
			pricesPresence,
		});

		const { onChange } = this.props;
		this.setState({ showSettingsModal: false });

		const newFilterParameters = [...this.filterFormElement.getCurrentFilterParameters()] as ProductFilterFormFilters;
		let hasChange = false;
		const datasourceParameterIndex = newFilterParameters.findIndex(
			(parameter) => parameter.name === FilterParameterName.datasource
		);

		if (types) {
			hasChange = true;
			const filterParameter = new FilterParameter<string[], FilterParameterName.datasource>(
				FilterParameterName.datasource,
				Array.from(types)
			);

			if (datasourceParameterIndex >= 0) {
				newFilterParameters[datasourceParameterIndex] = filterParameter;
			} else {
				newFilterParameters.push(filterParameter);
			}
		} else if (datasourceParameterIndex >= 0) {
			hasChange = true;
			newFilterParameters.splice(datasourceParameterIndex, 1);
		}

		const pricesPresenceIndex = newFilterParameters.findIndex(
			(parameter) => parameter.name === FilterParameterName.pricesPresence
		);

		if (pricesPresence) {
			hasChange = true;
			const filterParameter = new FilterParameter<PricesPresence, FilterParameterName.pricesPresence>(
				FilterParameterName.pricesPresence,
				pricesPresence
			);

			if (pricesPresenceIndex >= 0) {
				newFilterParameters[pricesPresenceIndex] = filterParameter;
			} else {
				newFilterParameters.push(filterParameter);
			}
		} else if (pricesPresenceIndex >= 0) {
			hasChange = true;
			newFilterParameters.splice(pricesPresenceIndex, 1);
		}

		const datasetPresenceIndex = newFilterParameters.findIndex(
			(parameter) => parameter.name === 'productDatasetPresence'
		);
		if (datasetPresence !== DatasetPresences.present) {
			hasChange = true;
			const filterParameter = new FilterParameter<DatasetPresence, FilterParameterName.productDatasetPresence>(
				FilterParameterName.productDatasetPresence,
				datasetPresence
			);

			if (datasetPresenceIndex >= 0) {
				newFilterParameters[datasetPresenceIndex] = filterParameter;
			} else {
				newFilterParameters.push(filterParameter);
			}
		} else if (datasetPresenceIndex >= 0) {
			hasChange = true;
			newFilterParameters.splice(datasetPresenceIndex, 1);
		}

		if (hasChange) {
			onChange(newFilterParameters);
		}
	};

	render() {
		const {
			props: { filterParameters, errors, onSearch },
			state: { showSettingsModal },
		} = this;
		const autocompletes = {
			country: (props: React.ComponentProps<typeof CountryAutocomplete>) => <CountryAutocomplete multiple {...props} />,
		};

		const parameterIndex = filterParameters.findIndex((parameter) => parameter.name === FilterParameterName.datasource);
		const parameter = parameterIndex >= 0 ? filterParameters[parameterIndex] : null;
		const dataSourceTypes = parameter !== null ? new Set(parameter.value) : null;
		const { pricesPresence } = this.props;
		const datasetPresence = this.props.datasetPresence || DatasetPresences.present;

		return (
			<Fragment>
				<FilterForm
					autocompletes={autocompletes}
					availableParameters={this.resolveAvailableParameters()}
					buttons={this.renderSettingsButton(dataSourceTypes, pricesPresence, datasetPresence)}
					errors={errors}
					filterParameters={filterParameters}
					ref={(ref) => (this.filterFormElement = ref)}
					onSearch={(filterParameters: ProductFilterFormFilters) => onSearch(filterParameters)}
				/>

				{showSettingsModal ? (
					<SearchSettingsModal
						datasetPresence={datasetPresence}
						dataSourceTypes={dataSourceTypes}
						pricesPresence={pricesPresence}
						selectedGrouppingType={this.props.selectedGrouppingType}
						onClose={() => this.setState({ showSettingsModal: false })}
						onConfirm={this.handleSettingsConfirmed}
					/>
				) : null}
			</Fragment>
		);
	}
}
